/**
 * Utilitários de validação para formulários
 */

/**
 * Valida CPF brasileiro
 * @param cpf - CPF a ser validado (com ou sem formatação)
 * @returns true se válido, false caso contrário
 */
export function validateCPF(cpf: string): boolean {
  // Remove caracteres não numéricos
  const cleanCPF = cpf.replace(/\D/g, "");

  // Verifica se tem 11 dígitos
  if (cleanCPF.length !== 11) return false;

  // Verifica se todos os dígitos são iguais (CPF inválido)
  if (/^(\d)\1{10}$/.test(cleanCPF)) return false;

  // Valida primeiro dígito verificador
  let sum = 0;
  for (let i = 0; i < 9; i++) {
    sum += parseInt(cleanCPF.charAt(i)) * (10 - i);
  }
  let digit = 11 - (sum % 11);
  if (digit >= 10) digit = 0;
  if (digit !== parseInt(cleanCPF.charAt(9))) return false;

  // Valida segundo dígito verificador
  sum = 0;
  for (let i = 0; i < 10; i++) {
    sum += parseInt(cleanCPF.charAt(i)) * (11 - i);
  }
  digit = 11 - (sum % 11);
  if (digit >= 10) digit = 0;
  if (digit !== parseInt(cleanCPF.charAt(10))) return false;

  return true;
}

/**
 * Formata CPF (000.000.000-00)
 */
export function formatCPF(cpf: string): string {
  const cleanCPF = cpf.replace(/\D/g, "");
  if (cleanCPF.length <= 3) return cleanCPF;
  if (cleanCPF.length <= 6)
    return `${cleanCPF.slice(0, 3)}.${cleanCPF.slice(3)}`;
  if (cleanCPF.length <= 9)
    return `${cleanCPF.slice(0, 3)}.${cleanCPF.slice(3, 6)}.${cleanCPF.slice(6)}`;
  return `${cleanCPF.slice(0, 3)}.${cleanCPF.slice(3, 6)}.${cleanCPF.slice(6, 9)}-${cleanCPF.slice(9, 11)}`;
}

/**
 * Valida domínio de email
 * Verifica se o domínio não está em lista de domínios temporários/descartáveis
 * @param email - Email a ser validado
 * @returns objeto com isValid e reason
 */
export async function validateEmailDomain(
  email: string,
): Promise<{ isValid: boolean; reason?: string }> {
  if (!email || !email.includes("@")) {
    return { isValid: false, reason: "Email inválido" };
  }

  const domain = email.split("@")[1]?.toLowerCase();
  if (!domain) {
    return { isValid: false, reason: "Domínio inválido" };
  }

  // Lista de domínios temporários/descartáveis conhecidos
  const disposableDomains = [
    "tempmail.com",
    "guerrillamail.com",
    "mailinator.com",
    "10minutemail.com",
    "throwaway.email",
    "temp-mail.org",
    "getnada.com",
    "mohmal.com",
    "yopmail.com",
    "sharklasers.com",
    "grr.la",
    "guerrillamailblock.com",
    "pokemail.net",
    "spam4.me",
    "bccto.me",
    "chacuo.net",
    "dispostable.com",
    "fakeinbox.com",
    "meltmail.com",
    "mintemail.com",
    "mytrashmail.com",
    "tempail.com",
    "tempmailaddress.com",
    "trashmail.com",
    "trashmailer.com",
    "getairmail.com",
    "maildrop.cc",
    "mailsac.com",
    "mailtemp.info",
    "mintemail.com",
    "mohmal.com",
    "mytrashmail.com",
    "spamgourmet.com",
    "temp-mail.io",
    "tempmailo.com",
    "throwawaymail.com",
    "tmpmail.org",
    "mailnesia.com",
    "getnada.com",
    "inboxkitten.com",
    "mailcatch.com",
    "mailforspam.com",
    "mailme.lv",
    "melt.li",
    "mintemail.com",
    "mohmal.com",
    "mytrashmail.com",
    "spamgourmet.com",
    "temp-mail.io",
    "tempmailo.com",
    "throwawaymail.com",
    "tmpmail.org",
    "mailnesia.com",
    "getnada.com",
    "inboxkitten.com",
    "mailcatch.com",
    "mailforspam.com",
    "mailme.lv",
    "melt.li",
    "mintemail.com",
    "mohmal.com",
    "mytrashmail.com",
    "spamgourmet.com",
    "temp-mail.io",
    "tempmailo.com",
    "throwawaymail.com",
    "tmpmail.org",
    "mailnesia.com",
    "getnada.com",
    "inboxkitten.com",
    "mailcatch.com",
    "mailforspam.com",
    "mailme.lv",
    "melt.li",
  ];

  if (disposableDomains.includes(domain)) {
    return {
      isValid: false,
      reason: "Domínios temporários não são permitidos",
    };
  }

  // Verificar formato básico do domínio
  const domainRegex =
    /^[a-z0-9]([a-z0-9-]{0,61}[a-z0-9])?(\.[a-z0-9]([a-z0-9-]{0,61}[a-z0-9])?)*$/i;
  if (!domainRegex.test(domain)) {
    return { isValid: false, reason: "Formato de domínio inválido" };
  }

  // Verificar se tem extensão válida (pelo menos 2 caracteres)
  const parts = domain.split(".");
  if (parts.length < 2 || parts[parts.length - 1].length < 2) {
    return { isValid: false, reason: "Domínio deve ter extensão válida" };
  }

  return { isValid: true };
}

/**
 * Valida formato de email
 */
export function validateEmailFormat(email: string): boolean {
  const emailRegex = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;
  return emailRegex.test(email);
}

/**
 * Valida telefone brasileiro
 * Aceita formatos: (11) 99999-9999, (11) 9999-9999, 11999999999, etc.
 * @param phone - Telefone a ser validado
 * @returns objeto com isValid e formatted
 */
export function validatePhone(phone: string): {
  isValid: boolean;
  formatted?: string;
  reason?: string;
} {
  // Remove caracteres não numéricos
  const cleanPhone = phone.replace(/\D/g, "");

  // Telefone brasileiro deve ter 10 ou 11 dígitos (com DDD)
  if (cleanPhone.length < 10 || cleanPhone.length > 11) {
    return { isValid: false, reason: "Telefone deve ter 10 ou 11 dígitos" };
  }

  // DDD deve estar entre 11 e 99
  const ddd = parseInt(cleanPhone.substring(0, 2));
  if (ddd < 11 || ddd > 99) {
    return { isValid: false, reason: "DDD inválido" };
  }

  // Formata telefone: (XX) XXXXX-XXXX ou (XX) XXXX-XXXX
  let formatted = "";
  if (cleanPhone.length === 11) {
    // Celular: (XX) XXXXX-XXXX
    formatted = `(${cleanPhone.substring(0, 2)}) ${cleanPhone.substring(2, 7)}-${cleanPhone.substring(7)}`;
  } else {
    // Fixo: (XX) XXXX-XXXX
    formatted = `(${cleanPhone.substring(0, 2)}) ${cleanPhone.substring(2, 6)}-${cleanPhone.substring(6)}`;
  }

  return { isValid: true, formatted };
}

/**
 * Formata telefone brasileiro
 */
export function formatPhone(phone: string): string {
  const cleanPhone = phone.replace(/\D/g, "");
  if (cleanPhone.length === 11) {
    return `(${cleanPhone.substring(0, 2)}) ${cleanPhone.substring(2, 7)}-${cleanPhone.substring(7)}`;
  } else if (cleanPhone.length === 10) {
    return `(${cleanPhone.substring(0, 2)}) ${cleanPhone.substring(2, 6)}-${cleanPhone.substring(6)}`;
  }
  return phone;
}
